// DateRangePicker.jsx

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Box,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import {
    LocalizationProvider,
    DatePicker,
    PickersDay,
} from "@mui/x-date-pickers";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
// import "dayjs/locale/en-ie";

import Colours from "../helpers/colours";
import { formatDate, getDatesBetween } from "../helpers/filters";
import { MAX_DATE, MIN_DATE } from "../helpers/dates";
import Icons from "../helpers/icons";

dayjs.extend(utc);
dayjs.extend(timezone);
// dayjs.locale("utc");

const DEFAULT_CUSTOM_VALUE = "CUSTOM";

const asDay = (d) => {
    if (!d) return null;

    const x = dayjs.isDayjs(d) ? d : dayjs(d);

    // Keep calendar dates as dates, not as timezone instants
    return dayjs.utc(x.format("YYYY-MM-DD"));
};

const clampDay = (d, min, max) => {
    const x = asDay(d);
    const mn = asDay(min);
    const mx = asDay(max);
    if (!x) return x;

    let out = x.startOf("day");
    if (mn && out.isBefore(mn, "day")) out = mn.startOf("day");
    if (mx && out.isAfter(mx, "day")) out = mx.startOf("day");
    return out;
};

const HighlightedDay = styled(PickersDay)(({ isSelected, isFromTo }) => ({
    ...(isSelected && {
        backgroundColor: isFromTo
            ? Colours.secondarySoft
            : Colours.tableRowBackground,
    }),
}));

const DefaultBoxProps = {
    sx: {
        height: "auto",
        // maxWidth: 370, // removed: no max width cap
        width: "fit-content", // added: shrink to content
        maxWidth: "none", // added: ensure no cap

        border: 1,
        borderRadius: 1,
        borderColor: Colours.midLightText,

        "& .MuiTextField-root": {
            border: "none",
            width: "auto",
            // "& .MuiOutlinedInput-input": {
            //     paddingTop: 0,
            //     paddingBottom: 0,
            //     paddingRight: 0,
            //     marginTop: "4px",
            //     marginBottom: "4px",
            //     width: "auto",
            // },
            "& fieldset": {
                border: "none",
            },
        },
    },
};

const DefaultFromDateProps = {
    value: dayjs.utc(),
    minDate: dayjs.utc(MIN_DATE),
    maxDate: dayjs.utc(MAX_DATE),
};

const DefaultToDateProps = {
    value: dayjs.utc(),
    maxDate: dayjs.utc(MAX_DATE),
};

const ITEM_HEIGHT = "50px";

const PICKER_TIMEZONE = "UTC";

const DateRangePicker = ({
    title = "",
    isRangePicker = true, // When set to false, the fromDate props are the one who controls the date picker
    onChangeFromDate,
    onChangeToDate,
    onChangeRange,
    fromDateProps = {},
    toDateProps = {},
    boxProps = { sx: { height: ITEM_HEIGHT } },
    open = false,
    pickByMonthYear = false,
    pickByYear = false,
    tittle_tooltip = null,
    tittle_tooltip_placement = "top",

    // presets support
    presets = null,
    presetValue,
    onChangePresetValue,
    customPreset = { value: DEFAULT_CUSTOM_VALUE, label: "Custom" },
}) => {
    const isPresetsEnabled = Array.isArray(presets) && presets.length > 0;
    const customValue = customPreset?.value ?? DEFAULT_CUSTOM_VALUE;

    const [internalPresetValue, setInternalPresetValue] = useState(customValue);

    const currentPresetValue =
        presetValue !== undefined ? presetValue : internalPresetValue;

    const selectedPreset = useMemo(() => {
        if (!isPresetsEnabled) return null;
        if (currentPresetValue === customValue) return null;
        return presets.find((p) => p.value === currentPresetValue) ?? null;
    }, [isPresetsEnabled, presets, currentPresetValue, customValue]);

    const isCustomMode =
        !isPresetsEnabled ||
        currentPresetValue === customValue ||
        selectedPreset == null;

    const [presetsAnchorEl, setPresetsAnchorEl] = useState(null);
    const presetsAnchorRef = useRef(null);
    const presetsMenuOpen = Boolean(presetsAnchorEl);

    const setPresetValueSafe = useCallback(
        (v) => {
            onChangePresetValue?.(v);
            if (presetValue === undefined) setInternalPresetValue(v);
        },
        [onChangePresetValue, presetValue],
    );

    const minAllowed = useMemo(() => {
        // Prefer explicit minDate from props (either side)
        return fromDateProps?.minDate ?? toDateProps?.minDate ?? null;
    }, [fromDateProps?.minDate, toDateProps?.minDate]);

    const maxAllowed = useMemo(() => {
        // Prefer explicit maxDate from props (either side)
        return fromDateProps?.maxDate ?? toDateProps?.maxDate ?? null;
    }, [fromDateProps?.maxDate, toDateProps?.maxDate]);

    const [openFromDatePicker, setOpenFromDatePicker] = useState(false);
    const [openEndDatePicker, setOpenEndDatePicker] = useState(false);

    // CHANGED: only auto-open the picker when we are in Custom mode
    useEffect(() => {
        if (!isCustomMode) return;
        setOpenFromDatePicker(open);
    }, [open, isCustomMode]);

    // CHANGED: if we leave Custom mode, close any open calendars
    useEffect(() => {
        if (isCustomMode) return;
        setOpenFromDatePicker(false);
        setOpenEndDatePicker(false);
    }, [isCustomMode]);

    const [fromDate, setFromDate] = useState(dayjs.utc());
    const [toDate, setToDate] = useState(dayjs.utc());

    useEffect(() => {
        const date = fromDateProps.hasOwnProperty("value")
            ? fromDateProps.value
            : DefaultFromDateProps.value;

        setFromDate(asDay(date));
    }, [fromDateProps]);

    useEffect(() => {
        const date = toDateProps.hasOwnProperty("value")
            ? toDateProps.value
            : DefaultToDateProps.value;
        setToDate(asDay(date));
    }, [toDateProps]);

    // Forcing date pickers to close when clicking outside the component (e.g., other page buttons)
    useEffect(() => {
        if (!openFromDatePicker && !openEndDatePicker) return;

        const handleMouseDown = (e) => {
            // Ignore clicks inside this component's container
            if (presetsAnchorRef.current?.contains(e.target)) return;
            // Ignore clicks inside MUI Popper elements (calendar popup)
            if (e.target.closest("[data-popper-placement]")) return;

            setOpenFromDatePicker(false);
            setOpenEndDatePicker(false);
        };

        document.addEventListener("mousedown", handleMouseDown, true);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown, true);
        };
    }, [openFromDatePicker, openEndDatePicker]);

    const isPeriodPicker = pickByMonthYear || pickByYear;

    const normalizePeriodDate = (newValue, type) => {
        const selected = newValue.utc();

        if (pickByYear) {
            const base = dayjs.utc().year(selected.year());

            if (type === "start") return base.startOf("year");
            if (type === "end") return base.endOf("year");

            return base;
        }

        const base = dayjs.utc().year(selected.year()).month(selected.month());

        if (type === "start") return base.startOf("month");
        if (type === "end") return base.endOf("month");

        return base;
    };

    const renderDay = (day, _selectedDates, pickersDayProps) => {
        if (isPeriodPicker) return <PickersDay {...pickersDayProps} />;

        const adjustedDay = day.startOf("day");
        const fromDay = fromDate.startOf("day");
        const toDay = toDate.startOf("day");

        const isWithinRange =
            adjustedDay.valueOf() >= fromDay.valueOf() &&
            adjustedDay.valueOf() <= toDay.valueOf();

        const isFromTo =
            adjustedDay.valueOf() === fromDay.valueOf() ||
            adjustedDay.valueOf() === toDay.valueOf();

        return (
            <HighlightedDay
                {...pickersDayProps}
                isSelected={isWithinRange}
                isFromTo={isFromTo}
            />
        );
    };

    const pickerViewProps = pickByYear
        ? {
              format: "YYYY",
              views: ["year"],
              openTo: "year",
          }
        : pickByMonthYear
          ? {
                format: "MMM YYYY",
                views: ["year", "month"],
                openTo: "month",
            }
          : { format: "DD MMM YYYY" };

    const DATE_INPUT_CH = pickByYear ? 4 : pickByMonthYear ? 8 : 10;

    const lastAppliedPresetRef = useRef(null);

    const applyPresetRange = useCallback(
        (opt) => {
            if (!opt) return;

            const raw =
                typeof opt.getRange === "function" ? opt.getRange() : opt.range;

            if (!raw) return;

            const nextFrom = clampDay(raw.from, minAllowed, maxAllowed);
            let nextTo = clampDay(raw.to, minAllowed, maxAllowed);

            // Safety: ensure to >= from
            if (nextFrom && nextTo && nextTo.isBefore(nextFrom, "day")) {
                nextTo = nextFrom;
            }

            // Close calendars when applying a preset
            setOpenFromDatePicker(false);
            setOpenEndDatePicker(false);

            // Keep internal state aligned (useful for renderDay highlighting if user goes back to Custom)
            if (nextFrom) setFromDate(nextFrom);
            if (nextTo) setToDate(nextTo);

            onChangeFromDate?.(nextFrom);
            onChangeToDate?.(nextTo);
        },
        [minAllowed, maxAllowed, onChangeFromDate, onChangeToDate],
    );

    // if parent sets presetValue (or initial mount), apply the preset range
    useEffect(() => {
        if (!isPresetsEnabled) return;
        if (isCustomMode) return;

        if (lastAppliedPresetRef.current === currentPresetValue) return;

        const opt = presets.find((p) => p.value === currentPresetValue);
        if (!opt) return;

        applyPresetRange(opt);
        lastAppliedPresetRef.current = currentPresetValue;
    }, [
        isPresetsEnabled,
        isCustomMode,
        currentPresetValue,
        presets,
        applyPresetRange,
    ]);

    const handleOpenPresetsMenu = (e) => {
        if (!isPresetsEnabled) return;
        e?.stopPropagation?.();
        setPresetsAnchorEl(presetsAnchorRef.current || e.currentTarget);
    };

    const handleClosePresetsMenu = () => {
        setPresetsAnchorEl(null);
    };

    const handleSelectPreset = (value) => {
        setPresetValueSafe(value);
        handleClosePresetsMenu();

        if (value === customValue) {
            lastAppliedPresetRef.current = value;
            // Mimic previous behavior: open calendar immediately
            setOpenFromDatePicker(true);
            setOpenEndDatePicker(false);

            return;
        }

        const opt = presets.find((p) => p.value === value);
        if (opt) {
            applyPresetRange(opt);
            lastAppliedPresetRef.current = value;
        }
    };

    // const DATE_INPUT_CH = pickByMonthYear ? 9 : 13;
    const CustomOrRangePickers = () => {
        if (!isCustomMode) {
            // PRESET MODE: show the label instead of date pickers
            return (
                <TextField
                    size="small"
                    value={selectedPreset?.label ?? ""}
                    onClick={handleOpenPresetsMenu}
                    sx={{
                        // width: "auto",
                        "& .MuiInputBase-input": {
                            width: "max-content",
                            whiteSpace: "nowrap",
                            width: "auto",
                            p: 0,
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Icons.Calendar />
                            </InputAdornment>
                        ),
                        inputProps: {
                            readOnly: true,
                            style: { cursor: "pointer" },
                        },
                    }}
                />
            );
        }

        // CUSTOM MODE: show normal date picker(s)
        return isRangePicker ? (
            <>
                {/* FROM DATE PICKER */}
                <DatePicker
                    timezone={PICKER_TIMEZONE}
                    open={openFromDatePicker}
                    onOpen={() => {
                        setOpenFromDatePicker(true);
                        setOpenEndDatePicker(false);
                    }}
                    onClose={() => {
                        setOpenFromDatePicker(false);
                    }}
                    onAccept={() => {
                        setTimeout(() => {
                            setOpenEndDatePicker(true);
                            setOpenFromDatePicker(false);
                        }, 0);
                    }}
                    value={dayjs.utc(fromDate)}
                    minDate={dayjs.utc(
                        fromDateProps.minDate ?? DefaultFromDateProps.minDate,
                    )}
                    maxDate={dayjs.utc(
                        fromDateProps.maxDate ?? DefaultFromDateProps.maxDate,
                    )}
                    onChange={(newValue) => {
                        if (!newValue) return;

                        // If user edits dates manually, force Custom
                        if (
                            isPresetsEnabled &&
                            currentPresetValue !== customValue
                        ) {
                            setPresetValueSafe(customValue);
                            lastAppliedPresetRef.current = customValue;
                        }

                        if (isPeriodPicker) {
                            const utcStart = normalizePeriodDate(
                                newValue,
                                "start",
                            );
                            setFromDate(utcStart);
                            onChangeFromDate?.(utcStart);

                            // If TO date is before FROM, move TO to end of same month
                            if (utcStart.isAfter(toDate)) {
                                const utcEnd = normalizePeriodDate(
                                    newValue,
                                    "end",
                                );
                                setToDate(utcEnd);
                                onChangeToDate?.(utcEnd);
                            }
                        } else {
                            const utcDate = dayjs
                                .utc()
                                .year(newValue.utc().year())
                                .month(newValue.utc().month())
                                .date(newValue.utc().date());

                            setFromDate(utcDate);

                            const currentTo = asDay(toDate);
                            if (
                                currentTo &&
                                utcDate.isAfter(currentTo, "day")
                            ) {
                                setToDate(utcDate);

                                // Notify both dates together to avoid parent state being overwritten
                                if (onChangeRange) {
                                    onChangeRange(utcDate, utcDate);
                                } else {
                                    onChangeFromDate?.(utcDate);
                                    onChangeToDate?.(utcDate);
                                }

                                return;
                            }
                            onChangeFromDate?.(utcDate);
                        }
                    }}
                    slotProps={{
                        popper: {
                            disablePortal: false,
                            keepMounted: false,
                            placement: "bottom-start",
                        },
                        textField: {
                            onClick: () => {
                                setOpenFromDatePicker(true);
                                setOpenEndDatePicker(false);
                            },
                            sx: {
                                width: `${DATE_INPUT_CH + 3}ch`,
                                minWidth: 0,
                                "& .MuiInputBase-root": {
                                    width: "100%",
                                },
                                "& .MuiInputBase-input": {
                                    width: `${DATE_INPUT_CH}ch`,
                                    minWidth: 0,
                                    whiteSpace: "nowrap",
                                },
                            },
                            InputProps: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icons.Calendar />
                                    </InputAdornment>
                                ),
                                endAdornment: null,
                                inputProps: {
                                    readOnly: true,
                                    size: DATE_INPUT_CH,
                                },
                            },
                        },
                    }}
                    renderDay={renderDay}
                    {...pickerViewProps}
                />
                <Typography variant="body2" sx={{ margin: "0 8px" }}>
                    -
                </Typography>

                {/* TO DATE PICKER */}
                <DatePicker
                    timezone={PICKER_TIMEZONE}
                    open={openEndDatePicker}
                    onOpen={() => {
                        setOpenEndDatePicker(true);
                        setOpenFromDatePicker(false);
                    }}
                    onClose={() => setOpenEndDatePicker(false)}
                    disableOpenPicker
                    value={dayjs.utc(toDate)}
                    minDate={fromDate}
                    maxDate={dayjs.utc(
                        toDateProps.maxDate ?? DefaultToDateProps.maxDate,
                    )}
                    onChange={(newValue) => {
                        if (!newValue) return;

                        // If user edits dates manually, force Custom
                        if (
                            isPresetsEnabled &&
                            currentPresetValue !== customValue
                        ) {
                            setPresetValueSafe(customValue);
                            lastAppliedPresetRef.current = customValue;
                        }

                        if (isPeriodPicker) {
                            const utcEnd = normalizePeriodDate(newValue, "end");
                            setToDate(utcEnd);
                            onChangeToDate?.(utcEnd);
                        } else {
                            const utcDate = dayjs
                                .utc()
                                .year(newValue.utc().year())
                                .month(newValue.utc().month())
                                .date(newValue.utc().date());

                            setToDate(utcDate);
                            onChangeToDate?.(utcDate);
                        }
                    }}
                    slotProps={{
                        popper: {
                            disablePortal: false,
                            keepMounted: false,
                            placement: "bottom-start",
                        },
                        textField: {
                            sx: {
                                width: `${DATE_INPUT_CH + 3}ch`,
                                minWidth: 0,
                                "& .MuiInputBase-root": {
                                    width: "100%",
                                },
                                "& .MuiInputBase-input": {
                                    width: `${DATE_INPUT_CH}ch`,
                                    minWidth: 0,
                                    whiteSpace: "nowrap",
                                },
                            },
                            onClick: () => {
                                setOpenEndDatePicker(true);
                                setOpenFromDatePicker(false);
                            },
                            InputProps: {
                                inputProps: {
                                    readOnly: true,
                                },
                            },
                        },
                    }}
                    renderDay={renderDay}
                    {...pickerViewProps}
                />
            </>
        ) : (
            // SINGLE PICKER MODE
            <DatePicker
                timezone={PICKER_TIMEZONE}
                open={openFromDatePicker}
                onOpen={() => {
                    setOpenFromDatePicker(true);
                    setOpenEndDatePicker(false);
                }}
                onClose={() => setOpenFromDatePicker(false)}
                slotProps={{
                    popper: {
                        disablePortal: false,
                        keepMounted: false,
                        placement: "bottom-start",
                    },
                    textField: {
                        sx: {
                            width: "auto",
                            "& .MuiInputBase-input": {
                                width: `${DATE_INPUT_CH}ch`,
                                minWidth: 0,
                                whiteSpace: "nowrap",
                            },
                        },
                        onClick: () => {
                            setOpenFromDatePicker(true);
                            setOpenEndDatePicker(false);
                        },
                        InputProps: {
                            inputProps: {
                                readOnly: true,
                            },
                        },
                    },
                }}
                value={dayjs.utc(fromDate)}
                minDate={dayjs.utc(
                    fromDateProps.minDate ?? DefaultFromDateProps.minDate,
                )}
                maxDate={dayjs.utc(
                    fromDateProps.maxDate ?? DefaultFromDateProps.maxDate,
                )}
                onChange={(newValue) => {
                    if (!newValue) return;

                    // If user edits dates manually, force Custom
                    if (
                        isPresetsEnabled &&
                        currentPresetValue !== customValue
                    ) {
                        setPresetValueSafe(customValue);
                        lastAppliedPresetRef.current = customValue;
                    }

                    if (pickByMonthYear) {
                        const utcStart = normalizePeriodDate(newValue, "start");
                        setFromDate(utcStart);
                        onChangeFromDate?.(utcStart);
                    } else {
                        const utcDate = dayjs
                            .utc()
                            .year(newValue.utc().year())
                            .month(newValue.utc().month())
                            .date(newValue.utc().date());
                        setFromDate(utcDate);
                        onChangeFromDate?.(utcDate);
                    }
                }}
                renderDay={renderDay}
                {...pickerViewProps}
            />
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                {tittle_tooltip ? (
                    <Tooltip
                        title={tittle_tooltip}
                        placement={tittle_tooltip_placement}
                        arrow
                    >
                        <Typography>{title}</Typography>
                    </Tooltip>
                ) : (
                    <Typography>{title}</Typography>
                )}

                <Box
                    ref={presetsAnchorRef}
                    display="inline-flex"
                    alignItems="center"
                    sx={{
                        ...DefaultBoxProps.sx,
                        ...(boxProps.sx ?? {}),

                        // removed: ...(isRangePicker ? {} : { maxWidth: 200 }),

                        // added: force shrink-to-fit even if boxProps had maxWidth
                        maxWidth: "none",
                        width: "fit-content",
                    }}
                >
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ flex: "0 0 auto" }}
                        // If we are in preset mode, clicking the body should open the presets menu
                        onClick={
                            !isCustomMode ? handleOpenPresetsMenu : undefined
                        }
                    >
                        <CustomOrRangePickers />
                    </Box>

                    {/* dropdown arrow when presets are enabled */}
                    {isPresetsEnabled && (
                        <Box
                            onClick={handleOpenPresetsMenu}
                            sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                px: 0.5,
                                userSelect: "none",
                            }}
                        >
                            <ArrowDropDownIcon />
                        </Box>
                    )}
                </Box>

                {/* presets menu */}
                {isPresetsEnabled && (
                    <Menu
                        anchorEl={presetsAnchorEl}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        open={presetsMenuOpen}
                        onClose={handleClosePresetsMenu}
                        PaperProps={{ sx: { maxHeight: 280 } }}
                    >
                        {presets.map((p) => (
                            <MenuItem
                                key={p.value}
                                selected={currentPresetValue === p.value}
                                onClick={() => handleSelectPreset(p.value)}
                            >
                                {p.label}
                            </MenuItem>
                        ))}
                        <MenuItem
                            selected={currentPresetValue === customValue}
                            onClick={() => handleSelectPreset(customValue)}
                        >
                            {customPreset?.label ?? "Custom"}
                        </MenuItem>
                    </Menu>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default DateRangePicker;
